import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Grid, Card, CardContent, Typography, TextField, Select, MenuItem,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
    IconButton, Tooltip, Collapse, Tabs, Tab, Checkbox, FormControlLabel, Radio,
    RadioGroup, FormControl, FormLabel, Alert
} from '@mui/material';
import {
    Add, Refresh, ExpandMore, ExpandLess, Edit, CheckCircle, Block,
    Inbox, Visibility, HowToReg, Cancel, PriceCheck, AssignmentTurnedIn,
    Timeline, Send, LockReset, ContentCopy, Download, ReceiptLong, ManageAccounts
} from '@mui/icons-material';
import {
    BarChart as ActivityBarChart,
    Bar as ActivityBar,
    LineChart as ActivityLineChart,
    Line as ActivityLine,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ActivityChartTooltip,
    ResponsiveContainer
} from 'recharts';
import OvalSpinner from '../components/UI/Spinners/OvalSpinner';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import styles from './Analytics.module.css';
import { useNavigate } from 'react-router-dom';
import { useErrorSetter } from '../hooks/useUiMessages';
import { checkAuthenticatedAdmin } from '../utils/client-functions';

const token = () => sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');

// Uses local calendar parts (not toISOString, which shifts to UTC and can land
// on the wrong day for timezones ahead of UTC, e.g. WAT) so the date picker
// always defaults to the admin's actual local "today".
const activityDateValue = (daysFromToday = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatActivityDate = (value, withTime = false) => {
    if (!value) return '–';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '–';
    return date.toLocaleString([], withTime ? undefined : { dateStyle: 'medium' });
};

const formatActivityData = (eventData = {}) => Object.entries(eventData)
    .filter(([key]) => key !== 'schoolId')
    .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`)
    .join(' · ');

const STATUS_COLORS = { active: 'success', pending: 'warning', suspended: 'error' };
const PLAN_COLORS   = { standard: 'primary', premium: 'secondary' };

const REQ_STATUS_COLORS = { pending: 'warning', reviewing: 'info', approved: 'success', rejected: 'error' };
const REQ_STATUSES      = ['pending','reviewing','approved','rejected'];

const EMPTY_FORM = {
    // School fields
    name: '', contactEmail: '', phone: '', address: '',
    type: 'secondary', country: 'Nigeria', state: '',
    academicSession: '', currentTerm: 'first',
    // Admin fields
    adminFirstName: '', adminLastName: '', adminEmail: '', adminPhone: '',
    // Plan
    plan: 'premium',
    // Tracks the source request (auto-marked converted on school creation)
    requestId: ''
};

const SCHOOL_TYPES   = ['primary','secondary','tertiary','other'];
const TERMS          = ['first','second','third'];
const PLANS          = ['standard','premium'];
const SUB_ADMIN_ACCESS_OPTIONS = [
    ['students', 'Students'], ['parents', 'Parents'], ['teachers', 'Teachers'],
    ['classes', 'Classes'], ['curriculum', 'Curriculum'], ['exams', 'Exams'],
    ['fees', 'Fees'], ['calendar', 'Calendar'], ['timetable', 'Timetable'],
    ['attendance', 'Attendance'], ['lesson-plans', 'Lesson Plans'], ['expenses', 'Expenses'],
    ['payroll', 'Payroll'], ['inventory', 'Inventory'], ['messages', 'Messages'],
    ['analytics', 'Analytics'], ['reports', 'Reports'], ['settings', 'Settings']
];
const EXAM_STATUS_OPTIONS = [
    ['draft', 'Draft'], ['published', 'Published'], ['scheduled', 'Scheduled'],
    ['closed', 'Closed'], ['pendingApproval', 'Pending approval']
];
const EXAM_MODE_OPTIONS = [
    ['cbt', 'CBT'], ['manual', 'Manual + AI grading'], ['manual_scores', 'Manual score entry'], ['other', 'Other']
];

// ─── Sub-component: subscription modal ───────────────────────────────────────
function SubscriptionDialog({ open, school, onClose, onSaved }) {
    const [form, setForm] = useState({ plan: 'premium', customPerTermPrice: '', maxStudents: '', maxTeachers: '', maxExamsPerTerm: '' });
    const [saving, setSaving] = useState(false);
    const setError = useErrorSetter();

    useEffect(() => {
        if (school) {
            const s = school.subscription || {};
            setForm({
                plan: s.plan || 'premium',
                customPerTermPrice: s.customPerTermPrice ?? '',
                maxStudents: s.maxStudents ?? '',
                maxTeachers: s.maxTeachers ?? '',
                maxExamsPerTerm: s.maxExamsPerTerm ?? ''
            });
        }
    }, [school]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const body = { plan: form.plan };
            body.customPerTermPrice = form.customPerTermPrice === '' ? null : Number(form.customPerTermPrice);
            if (form.maxStudents !== '') body.maxStudents = Number(form.maxStudents);
            if (form.maxTeachers !== '') body.maxTeachers = Number(form.maxTeachers);
            if (form.maxExamsPerTerm !== '') body.maxExamsPerTerm = Number(form.maxExamsPerTerm);
            await axios.patch(`/origin/schools/${school._id}/subscription`, body, {
                headers: { 'x-access-token': token() }
            });
            onSaved();
            onClose();
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to update subscription');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Update Subscription — {school?.name}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <Select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))} size="small" fullWidth>
                    {PLANS.map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
                </Select>
                <TextField
                    label="Negotiated Fee (₦ / student / term)"
                    type="number"
                    size="small"
                    value={form.customPerTermPrice}
                    inputProps={{ min: 0, step: 1 }}
                    onChange={e => setForm(f => ({ ...f, customPerTermPrice: e.target.value }))}
                    helperText="Blank uses the selected plan's default price. This override is private to this school."
                />
                <TextField label="Max Students" type="number" size="small" value={form.maxStudents}
                    onChange={e => setForm(f => ({ ...f, maxStudents: e.target.value }))} />
                <TextField label="Max Teachers" type="number" size="small" value={form.maxTeachers}
                    onChange={e => setForm(f => ({ ...f, maxTeachers: e.target.value }))} />
                <TextField label="Max Exams / Term" type="number" size="small" value={form.maxExamsPerTerm}
                    onChange={e => setForm(f => ({ ...f, maxExamsPerTerm: e.target.value }))} />
                <Typography variant="caption" color="text.secondary">
                    Leave a field blank to use the plan's default limits.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Sub-component: expandable school row ─────────────────────────────────────
function SchoolRow({ school, onStatusChange, onEditSubscription, onResetStudentPasswords, canResetStudents, onManageStudentStatus, onOpenInvoices }) {
    const [open, setOpen] = useState(false);
    const sub = school.subscription || {};

    const statusAction = school.status === 'active' ? 'suspended' : 'active';

    return (
        <>
            <TableRow hover>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(o => !o)}>
                        {open ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </TableCell>
                <TableCell><strong>{school.name}</strong></TableCell>
                <TableCell>{school.contactEmail}</TableCell>
                <TableCell>
                    <Chip label={school.status} color={STATUS_COLORS[school.status] || 'default'} size="small" />
                </TableCell>
                <TableCell>
                    <Chip label={sub.plan || 'basic'} color={PLAN_COLORS[sub.plan] || 'default'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{school.type}</TableCell>
                <TableCell>{school.state}</TableCell>
                <TableCell>
                    <Tooltip title="Active students (billable) / quota">
                        <span>{school.studentCount ?? 0} / {sub.maxStudents ?? '–'}</span>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    {school.teacherCount ?? 0} / {sub.maxTeachers ?? '–'}
                </TableCell>
                <TableCell>
                    <Tooltip title="Subscription invoices">
                        <IconButton size="small" color="primary" onClick={() => onOpenInvoices(school)}>
                            <ReceiptLong fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Student status">
                        <IconButton size="small" onClick={() => onManageStudentStatus(school)}>
                            <ManageAccounts fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={canResetStudents ? 'Reset student passwords' : 'Only admin and superadmin roles can reset student passwords'}>
                        <span>
                            <IconButton size="small" color="warning" disabled={!canResetStudents} onClick={() => onResetStudentPasswords(school)}>
                                <LockReset fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Edit Subscription">
                        <IconButton size="small" onClick={() => onEditSubscription(school)}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={statusAction === 'active' ? 'Activate' : 'Suspend'}>
                        <IconButton size="small" color={statusAction === 'suspended' ? 'error' : 'success'}
                            onClick={() => onStatusChange(school._id, statusAction)}>
                            {statusAction === 'suspended' ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={10} style={{ padding: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2"><strong>School Code:</strong> {school.schoolCode || '–'}</Typography>
                                    <Typography variant="body2"><strong>Address:</strong> {school.address || '–'}</Typography>
                                    <Typography variant="body2"><strong>Phone:</strong> {school.phone || '–'}</Typography>
                                    <Typography variant="body2"><strong>Country:</strong> {school.country || '–'}</Typography>
                                    <Typography variant="body2"><strong>Session:</strong> {school.academicSession || '–'}</Typography>
                                    <Typography variant="body2"><strong>Term:</strong> {school.currentTerm || '–'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2"><strong>Plan:</strong> {sub.plan}</Typography>
                                    <Typography variant="body2"><strong>AI Questions:</strong> {sub.aiQuestionsEnabled ? 'Yes' : 'No'}</Typography>
                                    <Typography variant="body2"><strong>Export:</strong> {sub.exportEnabled ? 'Yes' : 'No'}</Typography>
                                    <Typography variant="body2"><strong>Advanced Analytics:</strong> {sub.advancedAnalyticsEnabled ? 'Yes' : 'No'}</Typography>
                                    <Typography variant="body2"><strong>Sub. Ends:</strong> {sub.endDate ? new Date(sub.endDate).toLocaleDateString() : '–'}</Typography>
                                </Grid>
                            </Grid>
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<LockReset />}
                                disabled={!canResetStudents}
                                onClick={() => onResetStudentPasswords(school)}
                                sx={{ mt: 2 }}
                            >
                                Reset student passwords
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<ManageAccounts />} onClick={() => onManageStudentStatus(school)} sx={{ mt: 2, ml: 1 }}>
                                Student status
                            </Button>
                            <Button size="small" variant="outlined" color="primary" startIcon={<ReceiptLong />} onClick={() => onOpenInvoices(school)} sx={{ mt: 2, ml: 1 }}>
                                Subscription invoices
                            </Button>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

function StudentPasswordResetDialog({ school, onClose }) {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [classId, setClassId] = useState('');
    const [page, setPage] = useState(1);
    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, pages: 1 });
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [mode, setMode] = useState('generate');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [mustChangePassword, setMustChangePassword] = useState(true);
    const [results, setResults] = useState([]);
    const [customPasswordResult, setCustomPasswordResult] = useState('');
    const [step, setStep] = useState('select');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [copied, setCopied] = useState(false);
    const limit = 50;

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (classId) params.set('classId', classId);
        axios.get(`/origin/schools/${school._id}/students?${params.toString()}`, {
            headers: { 'x-access-token': token() },
        }).then(response => {
            if (cancelled) return;
            setStudents(response.data.students || []);
            setClasses(response.data.classes || []);
            setPagination(response.data.pagination || { total: 0, pages: 1 });
        }).catch(err => {
            if (!cancelled) setError(err?.response?.data?.error || 'Could not load this school\'s students.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [school._id, debouncedSearch, classId, page]);

    const currentPageIds = students.map(student => String(student.membershipId));
    const allCurrentSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.has(id));
    const someCurrentSelected = currentPageIds.some(id => selectedIds.has(id));
    const succeeded = results.filter(result => result.ok).length;
    const failed = results.length - succeeded;

    const toggleStudent = id => setSelectedIds(current => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });

    const toggleCurrentPage = () => setSelectedIds(current => {
        const next = new Set(current);
        if (allCurrentSelected) currentPageIds.forEach(id => next.delete(id));
        else currentPageIds.forEach(id => next.add(id));
        return next;
    });

    const beginConfirmation = () => {
        setError('');
        setFieldError('');
        if (selectedIds.size === 0) return setError('Select at least one student.');
        if (selectedIds.size > 200) return setError('Select no more than 200 students at once.');
        if (mode === 'custom') {
            if (newPassword.length < 8 || newPassword.length > 72) return setFieldError('Use a password between 8 and 72 characters.');
            if (newPassword !== confirmPassword) return setFieldError('Passwords do not match.');
        }
        setStep('confirm');
    };

    const resetPasswords = async () => {
        setError('');
        setFieldError('');
        if (!adminPassword) return setFieldError('Enter your admin password to confirm.');
        setSubmitting(true);
        try {
            const response = await axios.post(
                `/origin/schools/${school._id}/students/reset-passwords`,
                {
                    membershipIds: [...selectedIds],
                    mode,
                    ...(mode === 'custom' ? { newPassword, confirmPassword } : {}),
                    mustChangePassword,
                },
                {
                    headers: { 'x-access-token': token(), 'x-origin-admin-password': adminPassword },
                }
            );
            const responseResults = response.data.results || [];
            setResults(responseResults.map(result => ({
                ...result,
                password: result.ok ? (mode === 'generate' ? result.password : newPassword) : '',
            })));
            setCustomPasswordResult(mode === 'custom' && responseResults.some(result => result.ok) ? newPassword : '');
            setNewPassword('');
            setConfirmPassword('');
            setAdminPassword('');
            setStep('results');
        } catch (err) {
            const body = err?.response?.data || {};
            if (body.code === 'WRONG_ADMIN_PASSWORD') setFieldError(body.error || 'Your admin password is incorrect.');
            else if (body.field) setFieldError(body.error || 'Please check this field.');
            else setError(body.error || 'Could not reset the selected passwords.');
        } finally {
            setSubmitting(false);
        }
    };

    const csvCell = value => {
        let text = String(value ?? '');
        if (/^[=+\-@]/.test(text)) text = `'${text}`;
        return `"${text.replace(/"/g, '""')}"`;
    };

    const downloadResults = () => {
        const rows = [
            ['Student', 'Student ID', 'Email', 'New password', 'Status', 'Reason', 'Shared account'],
            ...results.map(result => [
                result.name || '', result.studentId || '', result.email || '', result.ok ? result.password : '',
                result.ok ? 'Reset' : 'Failed', result.reason || '', result.sharedAccount ? 'Yes' : 'No',
            ]),
        ];
        const csv = rows.map(row => row.map(csvCell).join(',')).join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `student-passwords-${school.schoolCode || school._id}-${new Date().toISOString().slice(0, 10)}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const copyPasswords = async () => {
        const text = results.filter(result => result.ok)
            .map(result => `${result.name || result.studentId || result.membershipId}: ${result.password}`)
            .join('\n');
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
        } catch {
            setError('Could not copy the passwords. Download the CSV instead.');
        }
    };

    return (
        <Dialog open onClose={() => !submitting && onClose()} maxWidth="lg" fullWidth>
            <DialogTitle>Reset student passwords — {school.name}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Students with an active session, including an exam session, will be signed out immediately. Shared Bubble Ai accounts will use this password everywhere.
                </Alert>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {step === 'select' && <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <TextField label="Search students" size="small" value={search} onChange={event => setSearch(event.target.value)} sx={{ minWidth: 240, flex: 1 }} />
                        <Select value={classId} onChange={event => { setClassId(event.target.value); setPage(1); }} displayEmpty size="small" sx={{ minWidth: 190, bgcolor: 'white' }}>
                            <MenuItem value="">All classes</MenuItem>
                            {classes.map(schoolClass => <MenuItem key={schoolClass._id} value={schoolClass._id}>{schoolClass.name || schoolClass.level}</MenuItem>)}
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2">{selectedIds.size} selected · {pagination.total} students</Typography>
                        {someCurrentSelected && <Button size="small" onClick={toggleCurrentPage}>{allCurrentSelected ? 'Deselect page' : 'Select page'}</Button>}
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={allCurrentSelected} indeterminate={someCurrentSelected && !allCurrentSelected} onChange={toggleCurrentPage} inputProps={{ 'aria-label': 'Select all students on this page' }} />
                                    </TableCell>
                                    <TableCell><strong>Student</strong></TableCell>
                                    <TableCell><strong>Student ID</strong></TableCell>
                                    <TableCell><strong>Class</strong></TableCell>
                                    <TableCell><strong>Account</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? <TableRow><TableCell colSpan={5} align="center">Loading students…</TableCell></TableRow>
                                    : students.length === 0 ? <TableRow><TableCell colSpan={5} align="center">No students found</TableCell></TableRow>
                                        : students.map(student => {
                                            const id = String(student.membershipId);
                                            return (
                                                <TableRow key={id} hover>
                                                    <TableCell padding="checkbox"><Checkbox checked={selectedIds.has(id)} onChange={() => toggleStudent(id)} inputProps={{ 'aria-label': `Select ${student.firstName} ${student.lastName}` }} /></TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>{student.firstName} {student.lastName}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{student.email}</Typography>
                                                    </TableCell>
                                                    <TableCell>{student.studentId || '—'}</TableCell>
                                                    <TableCell>{student.class?.name || student.class?.level || '—'}</TableCell>
                                                    <TableCell>{student.sharedAccount ? <Chip size="small" color="warning" label="Shared" /> : 'School account'}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 1 }}>
                        <Button size="small" disabled={page <= 1 || loading} onClick={() => setPage(value => value - 1)}>Previous</Button>
                        <Typography variant="caption">Page {page} of {Math.max(1, pagination.pages)}</Typography>
                        <Button size="small" disabled={page >= pagination.pages || loading} onClick={() => setPage(value => value + 1)}>Next</Button>
                    </Box>

                    <FormControl sx={{ mt: 2 }}>
                        <FormLabel>Password option</FormLabel>
                        <RadioGroup row value={mode} onChange={event => { setMode(event.target.value); setFieldError(''); }}>
                            <FormControlLabel value="generate" control={<Radio />} label="Generate unique passwords" />
                            <FormControlLabel value="custom" control={<Radio />} label="Use one password for all" />
                        </RadioGroup>
                    </FormControl>
                    {mode === 'custom' && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                            <TextField label="New password *" type="password" size="small" value={newPassword} onChange={event => setNewPassword(event.target.value)} error={Boolean(fieldError)} sx={{ flex: 1, minWidth: 220 }} />
                            <TextField label="Confirm password *" type="password" size="small" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} error={Boolean(fieldError)} sx={{ flex: 1, minWidth: 220 }} />
                        </Box>
                    )}
                    {fieldError && <Typography role="alert" variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{fieldError}</Typography>}
                    <FormControlLabel
                        sx={{ mt: 1 }}
                        control={<Checkbox checked={mustChangePassword} onChange={event => setMustChangePassword(event.target.checked)} />}
                        label="Require password change at next login"
                    />
                </>}

                {step === 'confirm' && <Box sx={{ py: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Confirm password reset</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {selectedIds.size} student{selectedIds.size === 1 ? '' : 's'} at {school.name} will receive {mode === 'generate' ? 'a unique generated password' : 'the same new password'}.
                        {mustChangePassword ? ' Students will be asked to change it at next login.' : ' Students will not be asked to change it at next login.'}
                    </Typography>
                    <TextField
                        label="Your admin password *"
                        type="password"
                        size="small"
                        fullWidth
                        value={adminPassword}
                        onChange={event => { setAdminPassword(event.target.value); setFieldError(''); }}
                        error={Boolean(fieldError)}
                        helperText={fieldError}
                        autoComplete="current-password"
                        onKeyDown={event => { if (event.key === 'Enter') resetPasswords(); }}
                    />
                </Box>}

                {step === 'results' && <>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip color="success" label={`${succeeded} reset`} />
                        <Chip color={failed ? 'error' : 'default'} label={`${failed} failed`} />
                        {mustChangePassword && <Chip variant="outlined" label="Change required at next login" />}
                    </Box>
                    {customPasswordResult && <Alert severity="info" sx={{ mb: 2 }}>
                        Password applied to successful students: <strong>{customPasswordResult}</strong>. This is the only time it is displayed.
                    </Alert>}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Tooltip title="Copy successful passwords"><span><IconButton onClick={copyPasswords} disabled={!succeeded}><ContentCopy /></IconButton></span></Tooltip>
                        <Tooltip title="Download results as CSV"><span><IconButton onClick={downloadResults} disabled={!results.length}><Download /></IconButton></span></Tooltip>
                        {copied && <Typography variant="caption" sx={{ alignSelf: 'center' }}>Passwords copied</Typography>}
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead><TableRow>
                                <TableCell><strong>Student</strong></TableCell>
                                <TableCell><strong>Student ID</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                {mode === 'generate' && <TableCell><strong>New password</strong></TableCell>}
                                <TableCell><strong>Result</strong></TableCell>
                            </TableRow></TableHead>
                            <TableBody>{results.map(result => <TableRow key={result.membershipId}>
                                <TableCell>{result.name || 'Unknown student'}{result.sharedAccount && <Chip size="small" color="warning" label="Shared" sx={{ ml: 1 }} />}</TableCell>
                                <TableCell>{result.studentId || '—'}</TableCell>
                                <TableCell>{result.email || '—'}</TableCell>
                                {mode === 'generate' && <TableCell sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{result.ok ? result.password : '—'}</TableCell>}
                                <TableCell>{result.ok ? <Chip size="small" color="success" label="Reset" /> : <Tooltip title={result.reason || ''}><Chip size="small" color="error" label={result.code || 'Failed'} /></Tooltip>}</TableCell>
                            </TableRow>)}</TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Passwords are shown once. Close this dialog to clear them from the page.
                    </Typography>
                </>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {step === 'select' && <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" onClick={beginConfirmation} disabled={loading || selectedIds.size === 0 || selectedIds.size > 200}>
                        Continue with {selectedIds.size}
                    </Button>
                </>}
                {step === 'confirm' && <>
                    <Button onClick={() => { setStep('select'); setFieldError(''); }}>Back</Button>
                    <Button variant="contained" color="warning" onClick={resetPasswords} disabled={submitting || !adminPassword}>
                        {submitting ? 'Resetting…' : `Reset ${selectedIds.size} password${selectedIds.size === 1 ? '' : 's'}`}
                    </Button>
                </>}
                {step === 'results' && <Button variant="contained" onClick={onClose}>Done</Button>}
            </DialogActions>
        </Dialog>
    );
}

const STUDENT_STATUS_OPTIONS = [
    ['active', 'Active'], ['graduated', 'Graduated'], ['transferred', 'Transferred'], ['removed', 'Removed']
];
const STUDENT_STATUS_COLORS = { active: 'success', graduated: 'info', transferred: 'warning', removed: 'error' };
const INVOICE_STATUS_COLORS = { issued: 'warning', paid: 'success', void: 'default' };

const formatNaira = value => `₦${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatTermLabel = term => term ? `${term.charAt(0).toUpperCase()}${term.slice(1)} Term` : '–';

const downloadOriginBlob = async (url, filename) => {
    const response = await axios.get(url, { headers: { 'x-access-token': token() }, responseType: 'blob' });
    const objectUrl = URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

// Blob error bodies need decoding before the JSON error message can be shown.
const blobErrorMessage = async (err, fallback) => {
    try {
        const data = err?.response?.data;
        if (data instanceof Blob) return JSON.parse(await data.text())?.error || fallback;
        return data?.error || fallback;
    } catch {
        return fallback;
    }
};

// ─── Bubble Ai admin: manual student status override ─────────────────────────
function StudentStatusDialog({ school, canEdit, onClose, onChanged }) {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [students, setStudents] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, pages: 1 });
    const [loading, setLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [target, setTarget] = useState(null);
    const [nextStatus, setNextStatus] = useState('active');
    const [reason, setReason] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const limit = 50;

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        const params = new URLSearchParams({ page: String(page), limit: String(limit), status: statusFilter });
        if (debouncedSearch) params.set('search', debouncedSearch);
        axios.get(`/origin/schools/${school._id}/students?${params.toString()}`, {
            headers: { 'x-access-token': token() },
        }).then(response => {
            if (cancelled) return;
            setStudents(response.data.students || []);
            setPagination(response.data.pagination || { total: 0, pages: 1 });
        }).catch(err => {
            if (!cancelled) setError(err?.response?.data?.error || 'Could not load this school\'s students.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [school._id, debouncedSearch, statusFilter, page, reloadKey]);

    const openChange = student => {
        setTarget(student);
        setNextStatus(student.status === 'active' ? 'removed' : 'active');
        setReason('');
        setAdminPassword('');
        setFieldError('');
        setError('');
        setSuccess('');
    };

    const submitChange = async () => {
        setFieldError('');
        setError('');
        if (nextStatus === target.status) return setFieldError('Choose a different status.');
        if (!reason.trim()) return setFieldError('Enter a reason (e.g. the support ticket or school request).');
        if (!adminPassword) return setFieldError('Enter your admin password to confirm.');
        setSubmitting(true);
        try {
            const response = await axios.patch(
                `/origin/schools/${school._id}/students/${target.membershipId}/status`,
                { status: nextStatus, reason: reason.trim() },
                { headers: { 'x-access-token': token(), 'x-origin-admin-password': adminPassword } }
            );
            setSuccess(response.data?.message || 'Student status updated.');
            setTarget(null);
            setAdminPassword('');
            setReloadKey(key => key + 1);
            if (onChanged) onChanged();
        } catch (err) {
            const body = err?.response?.data || {};
            if (body.field || body.code === 'WRONG_ADMIN_PASSWORD' || body.code === 'ADMIN_PASSWORD_REQUIRED') setFieldError(body.error || 'Please check this field.');
            else setError(body.error || 'Could not change the student status.');
        } finally {
            setSubmitting(false);
        }
    };

    const studentName = student => `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown student';

    return (
        <Dialog open onClose={() => !submitting && onClose()} maxWidth="lg" fullWidth>
            <DialogTitle>Student status — {school.name}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Use this when a school asks Bubble Ai support to change a student&apos;s status. This bypasses the school&apos;s term lock.
                    Billing counts only <strong>active</strong> students — re-activating a student adds them to this term&apos;s billable count.
                </Alert>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                {target ? (
                    <Box sx={{ py: 1 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Change status for {studentName(target)}</Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Current status: <Chip size="small" label={target.status} color={STUDENT_STATUS_COLORS[target.status] || 'default'} />
                            {target.locked && <Chip size="small" variant="outlined" color="warning" label="Locked for school admin" sx={{ ml: 1 }} />}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
                            <Select size="small" value={nextStatus} onChange={event => { setNextStatus(event.target.value); setFieldError(''); }}>
                                {STUDENT_STATUS_OPTIONS.map(([value, label]) => (
                                    <MenuItem key={value} value={value} disabled={value === target.status}>{label}</MenuItem>
                                ))}
                            </Select>
                            {target.status === 'active' && nextStatus !== 'active' && (
                                <Alert severity="warning">The school admin will not be able to return this student to Active until after the current term.</Alert>
                            )}
                            <TextField
                                label="Reason *"
                                size="small"
                                multiline
                                minRows={2}
                                value={reason}
                                inputProps={{ maxLength: 500 }}
                                onChange={event => { setReason(event.target.value); setFieldError(''); }}
                                helperText="Recorded in the audit log (e.g. support ticket number)."
                            />
                            <TextField
                                label="Your admin password *"
                                type="password"
                                size="small"
                                value={adminPassword}
                                onChange={event => { setAdminPassword(event.target.value); setFieldError(''); }}
                                autoComplete="current-password"
                                onKeyDown={event => { if (event.key === 'Enter') submitChange(); }}
                            />
                            {fieldError && <Typography role="alert" variant="caption" color="error">{fieldError}</Typography>}
                        </Box>
                    </Box>
                ) : <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <TextField label="Search students" size="small" value={search} onChange={event => setSearch(event.target.value)} sx={{ minWidth: 240, flex: 1 }} />
                        <Select value={statusFilter} onChange={event => { setStatusFilter(event.target.value); setPage(1); }} size="small" sx={{ minWidth: 170, bgcolor: 'white' }}>
                            <MenuItem value="all">All statuses</MenuItem>
                            {STUDENT_STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                        </Select>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{pagination.total} students</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Student</strong></TableCell>
                                    <TableCell><strong>Student ID</strong></TableCell>
                                    <TableCell><strong>Class</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Deactivated</strong></TableCell>
                                    <TableCell align="right"><strong>Action</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? <TableRow><TableCell colSpan={6} align="center">Loading students…</TableCell></TableRow>
                                    : students.length === 0 ? <TableRow><TableCell colSpan={6} align="center">No students found</TableCell></TableRow>
                                        : students.map(student => (
                                            <TableRow key={String(student.membershipId)} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>{studentName(student)}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{student.email}</Typography>
                                                </TableCell>
                                                <TableCell>{student.studentId || '—'}</TableCell>
                                                <TableCell>{student.class?.name || student.class?.level || '—'}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={student.status} color={STUDENT_STATUS_COLORS[student.status] || 'default'} />
                                                    {student.locked && <Tooltip title="The school admin cannot change this status until after the current term"><Chip size="small" variant="outlined" color="warning" label="Locked" sx={{ ml: 1 }} /></Tooltip>}
                                                </TableCell>
                                                <TableCell>
                                                    {student.deactivatedAt
                                                        ? `${formatActivityDate(student.deactivatedAt)}${student.deactivatedTerm ? ` · ${formatTermLabel(student.deactivatedTerm)}` : ''}${student.deactivatedSession ? ` ${student.deactivatedSession}` : ''}`
                                                        : '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={canEdit ? 'Change status' : 'Only admin and superadmin roles can change student status'}>
                                                        <span>
                                                            <Button size="small" variant="outlined" disabled={!canEdit} onClick={() => openChange(student)}>Change</Button>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 1 }}>
                        <Button size="small" disabled={page <= 1 || loading} onClick={() => setPage(value => value - 1)}>Previous</Button>
                        <Typography variant="caption">Page {page} of {Math.max(1, pagination.pages)}</Typography>
                        <Button size="small" disabled={page >= pagination.pages || loading} onClick={() => setPage(value => value + 1)}>Next</Button>
                    </Box>
                </>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {target ? <>
                    <Button onClick={() => { setTarget(null); setFieldError(''); }} disabled={submitting}>Back</Button>
                    <Button variant="contained" onClick={submitChange} disabled={submitting || !adminPassword || !reason.trim()}>
                        {submitting ? 'Saving…' : 'Change status'}
                    </Button>
                </> : <Button variant="contained" onClick={onClose}>Done</Button>}
            </DialogActions>
        </Dialog>
    );
}

// ─── Bubble Ai admin: per-term subscription invoice generator ─────────────────
function SubscriptionInvoiceDialog({ school, canEdit, onClose }) {
    const [preview, setPreview] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [discount, setDiscount] = useState('');
    const [dueDays, setDueDays] = useState('14');
    const [notes, setNotes] = useState('');
    const [deductInApp, setDeductInApp] = useState(true);
    const [duplicateWarning, setDuplicateWarning] = useState('');
    const [generating, setGenerating] = useState(false);
    const [busyInvoiceId, setBusyInvoiceId] = useState('');
    const [voidTarget, setVoidTarget] = useState(null);
    const [voidReason, setVoidReason] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        const headers = { 'x-access-token': token() };
        Promise.all([
            axios.get(`/origin/schools/${school._id}/subscription-invoices/preview`, { headers }),
            axios.get(`/origin/schools/${school._id}/subscription-invoices`, { headers }),
        ]).then(([previewRes, listRes]) => {
            if (cancelled) return;
            setPreview(previewRes.data.preview || null);
            setInvoices(listRes.data.invoices || []);
        }).catch(err => {
            if (!cancelled) setError(err?.response?.data?.error || 'Could not load invoice details.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [school._id, reloadKey]);

    const discountValue = Number(discount || 0);
    const deduction = preview && deductInApp ? Math.min(preview.inAppCollected || 0, preview.subtotal || 0) : 0;
    const estimatedTotal = preview ? Math.max(0, (preview.subtotal || 0) - deduction - (Number.isFinite(discountValue) ? discountValue : 0)) : 0;

    const downloadPdf = async invoice => {
        setError('');
        setBusyInvoiceId(String(invoice._id));
        try {
            await downloadOriginBlob(`/origin/schools/${school._id}/subscription-invoices/${invoice._id}/pdf`, `${invoice.invoiceNumber}.pdf`);
        } catch (err) {
            setError(await blobErrorMessage(err, 'Could not download the invoice PDF.'));
        } finally {
            setBusyInvoiceId('');
        }
    };

    const generate = async (allowDuplicate = false) => {
        setError('');
        setSuccess('');
        if (!Number.isFinite(discountValue) || discountValue < 0) return setError('Discount must be zero or a positive amount.');
        setGenerating(true);
        try {
            const response = await axios.post(`/origin/schools/${school._id}/subscription-invoices`, {
                discount: discountValue,
                dueDays: dueDays === '' ? undefined : Number(dueDays),
                notes,
                deductInAppCollected: deductInApp,
                allowDuplicate,
            }, { headers: { 'x-access-token': token() } });
            setDuplicateWarning('');
            setSuccess(response.data?.message || 'Invoice generated.');
            setDiscount('');
            setNotes('');
            setReloadKey(key => key + 1);
            if (response.data?.invoice) downloadPdf(response.data.invoice);
        } catch (err) {
            const body = err?.response?.data || {};
            if (body.code === 'INVOICE_EXISTS') setDuplicateWarning(body.error);
            else setError(body.error || 'Could not generate the invoice.');
        } finally {
            setGenerating(false);
        }
    };

    const changeStatus = async (invoice, status, reason = '') => {
        setError('');
        setSuccess('');
        setBusyInvoiceId(String(invoice._id));
        try {
            const response = await axios.patch(
                `/origin/schools/${school._id}/subscription-invoices/${invoice._id}/status`,
                { status, reason },
                { headers: { 'x-access-token': token() } }
            );
            setSuccess(response.data?.message || 'Invoice updated.');
            setVoidTarget(null);
            setVoidReason('');
            setReloadKey(key => key + 1);
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not update the invoice.');
        } finally {
            setBusyInvoiceId('');
        }
    };

    return (
        <Dialog open onClose={() => !generating && onClose()} maxWidth="lg" fullWidth>
            <DialogTitle>Subscription invoices — {school.name}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                {loading && !preview ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><OvalSpinner /></Box> : preview && <>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        Current term: {formatTermLabel(preview.term)} · {preview.academicSession || 'Session not set'}
                    </Typography>
                    {!preview.academicSession && (
                        <Alert severity="warning" sx={{ mb: 2 }}>This school has not set its academic session. Ask the school to set it in Settings before invoicing.</Alert>
                    )}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {[
                            ['Billable (active) students', preview.studentCount],
                            ['Excluded (inactive)', `${preview.inactiveStudentCount} · ${preview.statusBreakdown?.graduated || 0} grad / ${preview.statusBreakdown?.transferred || 0} trans / ${preview.statusBreakdown?.removed || 0} rem`],
                            ['Price per student / term', `${formatNaira(preview.perStudentPrice)} (${preview.priceSource === 'negotiated' ? 'negotiated' : `${preview.plan} plan`})`],
                            ['Subtotal', formatNaira(preview.subtotal)],
                            ['Collected in-app this term', formatNaira(preview.inAppCollected)],
                            ['Estimated total due', formatNaira(estimatedTotal)],
                        ].map(([label, value]) => (
                            <Grid item xs={12} sm={6} md={4} key={label}>
                                <Card variant="outlined">
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                                        <Typography variant="body1" fontWeight={700}>{value}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {canEdit && <>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                            <TextField label="Discount (₦)" type="number" size="small" value={discount} inputProps={{ min: 0, step: 1 }} onChange={event => setDiscount(event.target.value)} sx={{ width: 180 }} />
                            <TextField label="Due in (days)" type="number" size="small" value={dueDays} inputProps={{ min: 0, max: 120, step: 1 }} onChange={event => setDueDays(event.target.value)} sx={{ width: 150 }} />
                            <TextField label="Notes (shown on invoice)" size="small" value={notes} inputProps={{ maxLength: 1000 }} onChange={event => setNotes(event.target.value)} sx={{ flex: 1, minWidth: 240 }} />
                        </Box>
                        <FormControlLabel
                            control={<Checkbox checked={deductInApp} onChange={event => setDeductInApp(event.target.checked)} disabled={!preview.inAppCollected} />}
                            label={`Deduct amount already collected in-app (${formatNaira(preview.inAppCollected)})`}
                        />
                        {duplicateWarning && (
                            <Alert severity="warning" sx={{ mt: 1 }} action={
                                <Button color="inherit" size="small" disabled={generating} onClick={() => generate(true)}>Generate anyway</Button>
                            }>{duplicateWarning}</Alert>
                        )}
                        <Box sx={{ mt: 1, mb: 3 }}>
                            <Button variant="contained" startIcon={<ReceiptLong />} disabled={generating || !preview.academicSession} onClick={() => generate(false)}>
                                {generating ? 'Generating…' : 'Generate invoice for current term'}
                            </Button>
                        </Box>
                    </>}
                </>}

                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Invoice history</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Invoice</strong></TableCell>
                                <TableCell><strong>Period</strong></TableCell>
                                <TableCell><strong>Students</strong></TableCell>
                                <TableCell><strong>Total</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Issued</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.length === 0 ? <TableRow><TableCell colSpan={7} align="center">{loading ? 'Loading…' : 'No invoices yet'}</TableCell></TableRow>
                                : invoices.map(invoice => {
                                    const busy = busyInvoiceId === String(invoice._id);
                                    return (
                                        <TableRow key={invoice._id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{invoice.invoiceNumber}</Typography>
                                                {invoice.createdByName && <Typography variant="caption" color="text.secondary">by {invoice.createdByName}</Typography>}
                                            </TableCell>
                                            <TableCell>{formatTermLabel(invoice.term)} · {invoice.academicSession}</TableCell>
                                            <TableCell>{invoice.studentCount} × {formatNaira(invoice.perStudentPrice)}</TableCell>
                                            <TableCell>{formatNaira(invoice.total)}</TableCell>
                                            <TableCell>
                                                <Tooltip title={invoice.statusReason || ''}>
                                                    <Chip size="small" label={invoice.status} color={INVOICE_STATUS_COLORS[invoice.status] || 'default'} />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{formatActivityDate(invoice.issueDate)}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title="Download PDF">
                                                    <span><IconButton size="small" disabled={busy} onClick={() => downloadPdf(invoice)}><Download fontSize="small" /></IconButton></span>
                                                </Tooltip>
                                                {canEdit && invoice.status === 'issued' && (
                                                    <Tooltip title="Mark as paid">
                                                        <span><IconButton size="small" color="success" disabled={busy} onClick={() => changeStatus(invoice, 'paid')}><PriceCheck fontSize="small" /></IconButton></span>
                                                    </Tooltip>
                                                )}
                                                {canEdit && invoice.status === 'paid' && (
                                                    <Tooltip title="Mark as unpaid (re-issue)">
                                                        <span><IconButton size="small" color="warning" disabled={busy} onClick={() => changeStatus(invoice, 'issued')}><Refresh fontSize="small" /></IconButton></span>
                                                    </Tooltip>
                                                )}
                                                {canEdit && invoice.status !== 'void' && (
                                                    <Tooltip title="Void invoice">
                                                        <span><IconButton size="small" color="error" disabled={busy} onClick={() => { setVoidTarget(invoice); setVoidReason(''); }}><Cancel fontSize="small" /></IconButton></span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="contained" onClick={onClose} disabled={generating}>Done</Button>
            </DialogActions>

            {voidTarget && (
                <Dialog open onClose={() => setVoidTarget(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Void {voidTarget.invoiceNumber}?</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>A void invoice cannot be changed again. Generate a new invoice if a corrected one is needed.</Typography>
                        <TextField label="Reason *" size="small" fullWidth multiline minRows={2} value={voidReason} inputProps={{ maxLength: 500 }} onChange={event => setVoidReason(event.target.value)} autoFocus />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setVoidTarget(null)}>Cancel</Button>
                        <Button variant="contained" color="error" disabled={!voidReason.trim() || busyInvoiceId === String(voidTarget._id)} onClick={() => changeStatus(voidTarget, 'void', voidReason.trim())}>
                            Void invoice
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Dialog>
    );
}

// ─── Request Detail Dialog ────────────────────────────────────────────────────
function RequestDetailDialog({ open, request: req, onClose, onStatusChange, onConvert }) {
    const [status, setStatus]   = useState('');
    const [notes,  setNotes]    = useState('');
    const [saving, setSaving]   = useState(false);

    useEffect(() => {
        if (req) { setStatus(req.status); setNotes(req.reviewNotes || ''); }
    }, [req]);

    if (!req) return null;

    const handleSave = async () => {
        setSaving(true);
        await onStatusChange(req._id, status, notes);
        setSaving(false);
        onClose();
    };

    const detailRow = (label, value) => (
        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2" fontWeight={500}>{value || '–'}</Typography>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>School Request — {req.schoolName}</span>
                <Chip label={req.status} color={REQ_STATUS_COLORS[req.status]} size="small" />
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>School Information</Typography>
                {detailRow('School Name', req.schoolName)}
                {detailRow('Type', req.schoolType)}
                {detailRow('State', req.state)}
                {detailRow('City', req.city)}
                {detailRow('Address', req.address)}
                {detailRow('Country', req.country)}
                {detailRow('Est. Students', req.estimatedStudents)}
                {detailRow('Est. Teachers', req.estimatedTeachers)}
                {detailRow('Current Exam System', req.currentExamSystem)}

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>Contact Person</Typography>
                {detailRow('Name', `${req.contactFirstName} ${req.contactLastName}`)}
                {detailRow('Role', req.contactRole)}
                {detailRow('Email', req.contactEmail)}
                {detailRow('Phone', req.contactPhone)}

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>Plan Preference</Typography>
                {detailRow('Plan', req.plan?.charAt(0).toUpperCase() + req.plan?.slice(1))}
                {detailRow('Billing Cycle', req.billingCycle)}

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>Additional</Typography>
                {detailRow('Heard about us', req.heardAbout)}
                {detailRow('Additional info', req.additionalInfo)}
                {detailRow('Submitted', req.createdAt ? new Date(req.createdAt).toLocaleString() : '–')}

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>Review</Typography>
                <Select value={status} onChange={e => setStatus(e.target.value)} size="small" fullWidth sx={{ mb: 1.5 }}>
                    {REQ_STATUSES.map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
                </Select>
                <TextField
                    label="Review Notes (optional)"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="outlined"
                    startIcon={<HowToReg />}
                    onClick={() => { onClose(); onConvert(req); }}
                    sx={{ color: '#3e8f93', borderColor: '#3e8f93' }}
                >
                    Convert to School
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ background: '#3e8f93', '&:hover': { background: '#2c6b6f' } }}
                >
                    {saving ? 'Saving…' : 'Save Status'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────
// ─── Sub-component: Plan Pricing panel (single source of truth editor) ───────
// Lets the main Bubble Ai admin set per-plan per-term price, the discount %
// applied when a school pays per year instead of per term, and max teachers.
// maxStudents is intentionally not editable here — every plan is unlimited
// students by product decision. Saving here immediately changes what the
// public pricing page shows and what new schools / plan changes use.
function PlanPricingCard({ config, onSaved }) {
    const [form, setForm] = useState({
        perTermPrice: config.perTermPrice,
        yearlyDiscountPercent: config.yearlyDiscountPercent,
        maxTeachers: config.maxTeachers,
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved]   = useState(false);
    const setError = useErrorSetter();

    const setField = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setSaved(false); };

    const handleSave = async () => {
        setSaving(true);
        try {
            const body = {
                perTermPrice: Number(form.perTermPrice),
                yearlyDiscountPercent: Number(form.yearlyDiscountPercent),
                maxTeachers: Number(form.maxTeachers),
            };
            const resp = await axios.patch(`/origin/school-plans/${config.plan}`, body, {
                headers: { 'x-access-token': token() }
            });
            onSaved(resp.data.plan);
            setSaved(true);
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to update plan pricing');
        } finally {
            setSaving(false);
        }
    };

    const perYearOriginal = Number(form.perTermPrice || 0) * 3;
    const perYearPrice = Math.round(perYearOriginal * (1 - Number(form.yearlyDiscountPercent || 0) / 100));

    return (
        <Grid item xs={12} md={6}>
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight={700} textTransform="capitalize">{config.plan}</Typography>
                        <Chip label={config.plan} color={PLAN_COLORS[config.plan] || 'default'} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Per Term Price (₦, per student)"
                            size="small" type="number"
                            value={form.perTermPrice}
                            onChange={setField('perTermPrice')}
                        />
                        <TextField
                            label="Yearly Discount % (applied to 3× term price)"
                            size="small" type="number"
                            inputProps={{ min: 0, max: 100 }}
                            value={form.yearlyDiscountPercent}
                            onChange={setField('yearlyDiscountPercent')}
                        />
                        <TextField
                            label="Max Teachers"
                            size="small" type="number"
                            inputProps={{ min: 1 }}
                            value={form.maxTeachers}
                            onChange={setField('maxTeachers')}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Max Students: Unlimited (fixed for every plan) &nbsp;·&nbsp; Per year total: ₦{perYearPrice.toLocaleString('en-NG')}
                            {Number(form.yearlyDiscountPercent) > 0 && ` (from ₦${perYearOriginal.toLocaleString('en-NG')})`}
                        </Typography>
                        <Button
                            variant="contained" onClick={handleSave} disabled={saving}
                            sx={{ background: '#3e8f93', '&:hover': { background: '#2c6b6f' }, alignSelf: 'flex-start' }}
                        >
                            {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Changes'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
}

function PlanPricingPanel() {
    const [plans, setPlans]     = useState([]);
    const [loading, setLoading] = useState(true);
    const setError = useErrorSetter();

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await axios.get('/origin/school-plans', { headers: { 'x-access-token': token() } });
            setPlans(resp.data.plans || []);
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to load plan pricing');
        } finally {
            setLoading(false);
        }
    }, [setError]);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    const handleSaved = (updated) => {
        setPlans(prev => prev.map(p => p.plan === updated.plan ? { ...p, ...updated } : p));
    };

    if (loading) return <OvalSpinner />;

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is the single source of truth for the public pricing page, new school creation,
                self-serve plan changes, and automated billing. Changes apply immediately.
            </Typography>
            <Grid container spacing={2}>
                {plans.map(cfg => (
                    <PlanPricingCard key={cfg.plan} config={cfg} onSaved={handleSaved} />
                ))}
            </Grid>
        </Box>
    );
}

// ─── Sub-component: per-school billing drill-down dialog ────────────────────
function SchoolBillingDetailDialog({ open, schoolId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const setError = useErrorSetter();

    useEffect(() => {
        if (!open || !schoolId) return;
        setLoading(true);
        axios.get(`/origin/schools/${schoolId}/billing`, { headers: { 'x-access-token': token() } })
            .then(resp => setData(resp.data))
            .catch(e => setError(e?.response?.data?.message || 'Failed to load billing detail'))
            .finally(() => setLoading(false));
    }, [open, schoolId, setError]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Billing History — {data?.school?.name || '…'}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {loading ? <OvalSpinner /> : data ? (
                    <>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Billing Periods</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ background: '#f5f7fa' }}>
                                        <TableCell><strong>Term / Session</strong></TableCell>
                                        <TableCell><strong>₦/Student</strong></TableCell>
                                        <TableCell><strong>Students</strong></TableCell>
                                        <TableCell><strong>Due</strong></TableCell>
                                        <TableCell><strong>Collected</strong></TableCell>
                                        <TableCell><strong>Balance</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.periods || []).length === 0 ? (
                                        <TableRow><TableCell colSpan={7} align="center">No billing periods yet</TableCell></TableRow>
                                    ) : data.periods.map(p => (
                                        <TableRow key={p._id} hover>
                                            <TableCell style={{ textTransform: 'capitalize' }}>{p.term} · {p.academicSession}</TableCell>
                                            <TableCell>₦{Number(p.perStudentPrice || 0).toLocaleString('en-NG')}</TableCell>
                                            <TableCell>{p.studentCount || 0}</TableCell>
                                            <TableCell>₦{Number(p.amountDue).toLocaleString('en-NG')}</TableCell>
                                            <TableCell>₦{Number(p.amountCollected).toLocaleString('en-NG')}</TableCell>
                                            <TableCell>₦{Number(p.balanceDue).toLocaleString('en-NG')}</TableCell>
                                            <TableCell><Chip size="small" label={p.status} color={p.status === 'overdue' ? 'error' : 'success'} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Recent Deductions</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ background: '#f5f7fa' }}>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell><strong>Student</strong></TableCell>
                                        <TableCell><strong>Term / Session</strong></TableCell>
                                        <TableCell><strong>Amount</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.ledger || []).length === 0 ? (
                                        <TableRow><TableCell colSpan={4} align="center">No deductions yet</TableCell></TableRow>
                                    ) : data.ledger.map(l => (
                                        <TableRow key={l._id} hover>
                                            <TableCell>{new Date(l.deductedAt).toLocaleDateString()}</TableCell>
                                            <TableCell>{l.studentName}</TableCell>
                                            <TableCell style={{ textTransform: 'capitalize' }}>{l.billingPeriodRef?.term} · {l.billingPeriodRef?.session}</TableCell>
                                            <TableCell>₦{Number(l.amountDeducted).toLocaleString('en-NG')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Fee Waivers Granted by School</Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ background: '#f5f7fa' }}>
                                        <TableCell><strong>Date Waived</strong></TableCell>
                                        <TableCell><strong>Student</strong></TableCell>
                                        <TableCell><strong>Class</strong></TableCell>
                                        <TableCell><strong>Term / Session</strong></TableCell>
                                        <TableCell><strong>Total Fee</strong></TableCell>
                                        <TableCell><strong>Amount Paid</strong></TableCell>
                                        <TableCell><strong>Amount Waived</strong></TableCell>
                                        <TableCell><strong>Waived By</strong></TableCell>
                                        <TableCell><strong>Reason</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(!data.waivers || data.waivers.length === 0) ? (
                                        <TableRow><TableCell colSpan={9} align="center">No fee waivers recorded for this school</TableCell></TableRow>
                                    ) : data.waivers.map(w => (
                                        <TableRow key={w._id} hover>
                                            <TableCell>{new Date(w.waivedAt).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <strong>{w.studentName}</strong>
                                                <Typography variant="caption" display="block" color="text.secondary">{w.studentEmail}</Typography>
                                            </TableCell>
                                            <TableCell>{w.className}</TableCell>
                                            <TableCell style={{ textTransform: 'capitalize' }}>{w.term} · {w.session}</TableCell>
                                            <TableCell>₦{Number(w.totalAmount).toLocaleString('en-NG')}</TableCell>
                                            <TableCell>
                                                {Number(w.amountPaid || 0) > 0 ? (
                                                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                                        ₦{Number(w.amountPaid).toLocaleString('en-NG')}
                                                    </span>
                                                ) : <span style={{ color: '#90a4ae' }}>₦0</span>}
                                            </TableCell>
                                            <TableCell>
                                                <strong style={{ color: '#d97706' }}>
                                                    ₦{Number(w.amountWaived !== undefined ? w.amountWaived : (w.totalAmount - (w.amountPaid || 0))).toLocaleString('en-NG')}
                                                </strong>
                                            </TableCell>
                                            <TableCell>{w.waivedByName}</TableCell>
                                            <TableCell style={{ maxWidth: 220, wordBreak: 'break-word' }}>{w.waivedReason}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Sub-component: Revenue panel (Bubble Ai's own fee-funded subscription
// revenue tracking, across every school) ──────────────────────────────────────
function RevenuePanel() {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [detailSchoolId, setDetailSchoolId] = useState(null);
    const setError = useErrorSetter();
    const LIMIT = 20;

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await axios.get(`/origin/billing/overview?page=${page}&limit=${LIMIT}`, {
                headers: { 'x-access-token': token() }
            });
            setOverview(resp.data);
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to load revenue overview');
        } finally {
            setLoading(false);
        }
    }, [page, setError]);

    useEffect(() => { fetchOverview(); }, [fetchOverview]);

    if (loading && !overview) return <OvalSpinner />;
    if (!overview) return null;

    const { totals, schools, recentLedger } = overview;
    const totalPages = Math.max(1, Math.ceil((overview.total || 0) / LIMIT));

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined"><CardContent>
                        <Typography variant="body2" color="text.secondary">Collected This Period (all schools)</Typography>
                        <Typography variant="h5" fontWeight={700}>₦{Number(totals.totalCollected).toLocaleString('en-NG')}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined"><CardContent>
                        <Typography variant="body2" color="text.secondary">Outstanding Balance</Typography>
                        <Typography variant="h5" fontWeight={700} color={totals.totalOutstanding > 0 ? '#c62828' : 'inherit'}>
                            ₦{Number(totals.totalOutstanding).toLocaleString('en-NG')}
                        </Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined"><CardContent>
                        <Typography variant="body2" color="text.secondary">Schools Overdue</Typography>
                        <Typography variant="h5" fontWeight={700}>{totals.overdueSchools}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined"><CardContent>
                        <Typography variant="body2" color="text.secondary">Schools Tracked</Typography>
                        <Typography variant="h5" fontWeight={700}>{totals.schoolsCount}</Typography>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Per-School Billing (Current Period)</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ background: '#f5f7fa' }}>
                            <TableCell><strong>School</strong></TableCell>
                            <TableCell><strong>Plan</strong></TableCell>
                            <TableCell><strong>Negotiated ₦/Student</strong></TableCell>
                            <TableCell><strong>Students</strong></TableCell>
                            <TableCell><strong>Term / Session</strong></TableCell>
                            <TableCell><strong>Due</strong></TableCell>
                            <TableCell><strong>Collected</strong></TableCell>
                            <TableCell><strong>Balance</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {schools.length === 0 ? (
                            <TableRow><TableCell colSpan={10} align="center">No billing periods yet</TableCell></TableRow>
                        ) : schools.map(s => (
                            <TableRow key={s.billingId} hover>
                                <TableCell><strong>{s.schoolName}</strong></TableCell>
                                <TableCell><Chip size="small" label={s.plan} color={PLAN_COLORS[s.plan] || 'default'} variant="outlined" /></TableCell>
                                <TableCell>{s.customPerTermPrice != null ? `₦${Number(s.customPerTermPrice).toLocaleString('en-NG')}` : '– (plan default)'}</TableCell>
                                <TableCell>{s.studentCount}</TableCell>
                                <TableCell style={{ textTransform: 'capitalize' }}>{s.term} · {s.academicSession}</TableCell>
                                <TableCell>₦{Number(s.amountDue).toLocaleString('en-NG')}</TableCell>
                                <TableCell>₦{Number(s.amountCollected).toLocaleString('en-NG')}</TableCell>
                                <TableCell>₦{Number(s.balanceDue).toLocaleString('en-NG')}</TableCell>
                                <TableCell><Chip size="small" label={s.status} color={s.status === 'overdue' ? 'error' : 'success'} /></TableCell>
                                <TableCell>
                                    <Tooltip title="View Billing History">
                                        <IconButton size="small" onClick={() => setDetailSchoolId(s.schoolId)}>
                                            <Visibility fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
                    <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                    <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {page} / {totalPages}</Typography>
                    <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                </Box>
            )}

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Recent Deductions — Live Feed</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ background: '#f5f7fa' }}>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>School</strong></TableCell>
                            <TableCell><strong>Student</strong></TableCell>
                            <TableCell><strong>Term / Session</strong></TableCell>
                            <TableCell><strong>Amount</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(recentLedger || []).length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center">No deductions yet</TableCell></TableRow>
                        ) : recentLedger.map(l => (
                            <TableRow key={l._id} hover>
                                <TableCell>{new Date(l.deductedAt).toLocaleString()}</TableCell>
                                <TableCell>{l.schoolName}</TableCell>
                                <TableCell>{l.studentName}</TableCell>
                                <TableCell style={{ textTransform: 'capitalize' }}>{l.term} · {l.session}</TableCell>
                                <TableCell>₦{Number(l.amountDeducted).toLocaleString('en-NG')}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <SchoolBillingDetailDialog open={!!detailSchoolId} schoolId={detailSchoolId} onClose={() => setDetailSchoolId(null)} />
        </Box>
    );
}

// ─── Sub-component: Fee Waivers Panel (System-wide tracking across all schools) ──
function FeeWaiversPanel({ schoolsList = [] }) {
    const [waivers, setWaivers] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    // Filters
    const [selectedSchool, setSelectedSchool] = useState('');
    const [search, setSearch] = useState('');
    const [term, setTerm] = useState('');
    const [sessionFilter, setSessionFilter] = useState('');

    const setError = useErrorSetter();
    const LIMIT = 20;

    const fetchWaivers = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams({ page, limit: LIMIT });
            if (selectedSchool) p.set('schoolId', selectedSchool);
            if (search.trim()) p.set('search', search.trim());
            if (term) p.set('term', term);
            if (sessionFilter.trim()) p.set('session', sessionFilter.trim());

            const resp = await axios.get(`/origin/waivers?${p.toString()}`, {
                headers: { 'x-access-token': token() }
            });
            setWaivers(resp.data.waivers || []);
            setTotal(resp.data.total || 0);
            setTotalAmount(resp.data.totalWaivedAmount || 0);
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to load fee waivers');
        } finally {
            setLoading(false);
        }
    }, [page, selectedSchool, search, term, sessionFilter, setError]);

    useEffect(() => { fetchWaivers(); }, [fetchWaivers]);

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Track and audit all fee waivers granted by school administrators. Bubble Ai admin can review waiver reasons, affected students, classes, and follow up with schools to collect platform subscription fees.
            </Typography>

            {/* Summary cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Total Waived Invoices</Typography>
                            <Typography variant="h5" fontWeight={700}>{total}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Total Waived Value</Typography>
                            <Typography variant="h5" fontWeight={700} color="#d97706">
                                ₦{Number(totalAmount).toLocaleString('en-NG')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    placeholder="Search student name or email…"
                    size="small"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    sx={{ minWidth: 220, bgcolor: 'white' }}
                />

                <Select
                    value={selectedSchool}
                    onChange={e => { setSelectedSchool(e.target.value); setPage(1); }}
                    displayEmpty
                    size="small"
                    sx={{ minWidth: 180, bgcolor: 'white' }}
                >
                    <MenuItem value=""><em>All Schools</em></MenuItem>
                    {schoolsList.map(s => (
                        <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                    ))}
                </Select>

                <Select
                    value={term}
                    onChange={e => { setTerm(e.target.value); setPage(1); }}
                    displayEmpty
                    size="small"
                    sx={{ minWidth: 140, bgcolor: 'white' }}
                >
                    <MenuItem value=""><em>All Terms</em></MenuItem>
                    <MenuItem value="first">First Term</MenuItem>
                    <MenuItem value="second">Second Term</MenuItem>
                    <MenuItem value="third">Third Term</MenuItem>
                </Select>

                <TextField
                    placeholder="Session (e.g. 2024/2025)"
                    size="small"
                    value={sessionFilter}
                    onChange={e => { setSessionFilter(e.target.value); setPage(1); }}
                    sx={{ minWidth: 160, bgcolor: 'white' }}
                />

                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setSelectedSchool('');
                        setSearch('');
                        setTerm('');
                        setSessionFilter('');
                        setPage(1);
                    }}
                >
                    Reset Filters
                </Button>
            </Box>

            {/* Waivers Table */}
            {loading ? <OvalSpinner /> : (
                <>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ background: '#f5f7fa' }}>
                                    <TableCell><strong>Date Waived</strong></TableCell>
                                    <TableCell><strong>School</strong></TableCell>
                                    <TableCell><strong>Student</strong></TableCell>
                                    <TableCell><strong>Class</strong></TableCell>
                                    <TableCell><strong>Term / Session</strong></TableCell>
                                    <TableCell><strong>Total Fee</strong></TableCell>
                                    <TableCell><strong>Amount Paid</strong></TableCell>
                                    <TableCell><strong>Amount Waived</strong></TableCell>
                                    <TableCell><strong>Reason for Waiver</strong></TableCell>
                                    <TableCell><strong>Waived By</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {waivers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                            No fee waivers found matching the selected criteria.
                                        </TableCell>
                                    </TableRow>
                                ) : waivers.map(w => {
                                    const amountPaid = Number(w.amountPaid || 0);
                                    const totalAmount = Number(w.totalAmount || 0);
                                    const amountWaived = Number(w.amountWaived !== undefined ? w.amountWaived : Math.max(0, totalAmount - amountPaid));
                                    return (
                                        <TableRow key={w._id} hover>
                                            <TableCell>{new Date(w.waivedAt).toLocaleDateString()}</TableCell>
                                            <TableCell><strong>{w.schoolName}</strong></TableCell>
                                            <TableCell>
                                                <strong>{w.studentName}</strong>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {w.studentEmail}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{w.className}</TableCell>
                                            <TableCell style={{ textTransform: 'capitalize' }}>{w.term} · {w.session}</TableCell>
                                            <TableCell>₦{totalAmount.toLocaleString('en-NG')}</TableCell>
                                            <TableCell>
                                                {amountPaid > 0 ? (
                                                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                                        ₦{amountPaid.toLocaleString('en-NG')}
                                                    </span>
                                                ) : <span style={{ color: '#90a4ae' }}>₦0</span>}
                                            </TableCell>
                                            <TableCell>
                                                <strong style={{ color: '#d97706' }}>
                                                    ₦{amountWaived.toLocaleString('en-NG')}
                                                </strong>
                                            </TableCell>
                                            <TableCell style={{ maxWidth: 260, wordBreak: 'break-word', color: '#78350f', background: '#fffbeb', borderRadius: 4, padding: '6px 10px' }}>
                                                {w.waivedReason}
                                            </TableCell>
                                            <TableCell>
                                                <div>{w.waivedByName}</div>
                                                <Typography variant="caption" color="text.secondary">{w.waivedByEmail}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3, mb: 2 }}>
                            <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                            <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {page} / {totalPages} ({total} total)</Typography>
                            <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}

function ActivityInviteTable({ title, payload, kind, onResend, resending, canResend = true }) {
    const stats = payload?.stats || {};
    const [view, setView] = useState('pending');
    const views = {
        pending: { label: 'Pending', count: stats.pending || 0 },
        expired: { label: 'Expired', count: stats.expiredPending || 0 },
        active: { label: 'Active', count: stats.active || 0 }
    };
    const selectedView = payload?.[view] || { items: [], truncated: false };
    const invites = selectedView.items || [];
    const canResendSelected = canResend && view !== 'active';
    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {Object.entries(views).map(([value, option]) => (
                        <Chip
                            key={value}
                            clickable
                            size="small"
                            color={value === 'active' ? 'success' : value === 'expired' ? 'error' : 'warning'}
                            variant={view === value ? 'filled' : 'outlined'}
                            label={`${option.label} ${option.count}`}
                            onClick={() => setView(value)}
                        />
                    ))}
                    <Select
                        value={view}
                        onChange={event => setView(event.target.value)}
                        size="small"
                        sx={{ minWidth: 140 }}
                    >
                        {Object.entries(views).map(([value, option]) => (
                            <MenuItem key={value} value={value}>{option.label} ({option.count})</MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ background: '#f5f7fa' }}>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Invited</strong></TableCell>
                            <TableCell><strong>Expiry</strong></TableCell>
                            <TableCell align="right"><strong>{canResend ? 'Action' : 'Status'}</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invites.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center">No {views[view].label.toLowerCase()} accounts</TableCell></TableRow>
                        ) : invites.map(invite => {
                            const inviteKey = `${kind}:${invite._id}`;
                            const name = `${invite.firstName || ''} ${invite.lastName || ''}`.trim() || invite.email;
                            return (
                                <TableRow key={String(invite._id)} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{name}</Typography>
                                        {invite.roleTitle && <Typography variant="caption" color="text.secondary">{invite.roleTitle}</Typography>}
                                    </TableCell>
                                    <TableCell>{invite.email || '–'}</TableCell>
                                    <TableCell>{formatActivityDate(invite.createdAt)}</TableCell>
                                    <TableCell>
                                        {view === 'expired' ? (
                                            <Chip size="small" color="error" label="Expired" />
                                        ) : view === 'active' ? (
                                            <Chip size="small" color="success" label="Active" />
                                        ) : formatActivityDate(invite.inviteTokenExpiry)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {canResendSelected ? (
                                            <Tooltip title="Resend invite">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        disabled={resending === inviteKey}
                                                        onClick={() => onResend(kind, invite)}
                                                    >
                                                        <Send fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : <Typography variant="caption" color="text.secondary">{view === 'active' ? 'Active' : 'Owner access'}</Typography>}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {selectedView.truncated && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Showing the first 200 {views[view].label.toLowerCase()} accounts.
                </Typography>
            )}
        </Box>
    );
}

function ExamSummaryCell({ exams = {} }) {
    const statusSummary = EXAM_STATUS_OPTIONS
        .filter(([key]) => Number(exams[key] || 0) > 0)
        .map(([key, label]) => `${label} ${exams[key]}`)
        .join(' · ');
    const modeSummary = EXAM_MODE_OPTIONS
        .filter(([key]) => Number(exams.byMode?.[key] || 0) > 0)
        .map(([key, label]) => `${label} ${exams.byMode[key]}`)
        .join(' · ');
    return (
        <Box sx={{ minWidth: 150 }}>
            <Typography variant="body2" fontWeight={700}>{exams.total || 0} total</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{statusSummary || 'No lifecycle data'}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{modeSummary || 'No mode data'}</Typography>
        </Box>
    );
}

function ExamBreakdownPanel({ exams = {} }) {
    const statusColors = { draft: 'default', published: 'success', scheduled: 'info', closed: 'default', pendingApproval: 'warning' };
    const modeColors = { cbt: 'primary', manual: 'secondary', manual_scores: 'warning', other: 'default' };
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Exam Breakdown</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>By lifecycle status</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {EXAM_STATUS_OPTIONS.map(([key, label]) => (
                    <Chip key={key} size="small" color={statusColors[key]} variant="outlined" label={`${label} ${exams[key] || 0}`} />
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>By exam mode</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {EXAM_MODE_OPTIONS.map(([key, label]) => (
                    <Chip key={key} size="small" color={modeColors[key]} variant="outlined" label={`${label} ${exams.byMode?.[key] || 0}`} />
                ))}
            </Box>
        </Box>
    );
}

function InviteResendEditDialog({ target, schoolId, students = [], onClose, onSaved }) {
    const actor = target?.actor;
    const kind = target?.kind;
    const [form, setForm] = useState({ email: '', firstName: '', lastName: '', phone: '', staffId: '', roleTitle: '' });
    const [membershipIds, setMembershipIds] = useState([]);
    const [relationship, setRelationship] = useState('guardian');
    const [accessTabs, setAccessTabs] = useState(['overview']);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!actor) return;
        setForm({
            email: actor.email || '',
            firstName: actor.firstName || '',
            lastName: actor.lastName || '',
            phone: actor.phone || '',
            staffId: actor.staffId || '',
            roleTitle: actor.roleTitle || ''
        });
        setMembershipIds((actor.linkedStudents || []).map(link => String(link.membershipId?._id || link.membershipId)));
        setRelationship(actor.linkedStudents?.[0]?.relationship || 'guardian');
        setAccessTabs([...new Set(['overview', ...(actor.accessTabs || [])])]);
        setError('');
    }, [actor, kind]);

    const setField = field => event => setForm(current => ({ ...current, [field]: event.target.value }));
    const toggleMembership = membershipId => setMembershipIds(current => current.includes(membershipId)
        ? current.filter(id => id !== membershipId)
        : [...current, membershipId]);
    const toggleAccess = accessKey => setAccessTabs(current => current.includes(accessKey)
        ? current.filter(key => key !== accessKey)
        : [...current, accessKey]);

    const handleSubmit = async event => {
        event.preventDefault();
        if (!target || !schoolId) return;
        setSaving(true);
        try {
            const body = { ...form };
            if (kind === 'parents') {
                body.membershipIds = membershipIds;
                body.relationship = relationship;
            }
            if (kind === 'subAdmins') body.accessTabs = accessTabs;
            const path = kind === 'teachers'
                ? `teachers/${actor._id}`
                : kind === 'parents'
                    ? `parents/${actor._id}`
                    : `sub-admins/${actor._id}`;
            const resp = await axios.post(`/origin/schools/${schoolId}/invites/${path}/resend`, body, {
                headers: { 'x-access-token': token() }
            });
            onSaved(resp.data);
        } catch (error) {
            setError(error?.response?.data?.error || 'Failed to update and resend invite');
        } finally {
            setSaving(false);
        }
    };

    const title = kind === 'teachers' ? 'Teacher' : kind === 'parents' ? 'Parent' : 'Sub-admin';
    return (
        <Dialog open={Boolean(target)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Edit and Resend {title} Invite</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Review or update the invite details before sending a fresh 48-hour activation link.
                    </Typography>
                    {error && <Typography color="error" variant="body2">{error}</Typography>}
                    <TextField label="Email" type="email" required size="small" value={form.email} onChange={setField('email')} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="First name" required size="small" fullWidth value={form.firstName} onChange={setField('firstName')} />
                        <TextField label="Last name" required size="small" fullWidth value={form.lastName} onChange={setField('lastName')} />
                    </Box>
                    <TextField label="Phone" size="small" value={form.phone} onChange={setField('phone')} />

                    {kind === 'teachers' && (
                        <TextField label="Staff ID" required size="small" value={form.staffId} onChange={setField('staffId')} />
                    )}

                    {kind === 'parents' && (
                        <>
                            <Select value={relationship} onChange={event => setRelationship(event.target.value)} size="small" fullWidth>
                                <MenuItem value="mother">Mother</MenuItem>
                                <MenuItem value="father">Father</MenuItem>
                                <MenuItem value="guardian">Guardian</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Linked students</Typography>
                                {students.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No active students found.</Typography>
                                ) : (
                                    <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                        {students.map(student => {
                                            const membershipId = String(student._id);
                                            const studentName = `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || 'Unnamed student';
                                            return (
                                                <FormControlLabel
                                                    key={membershipId}
                                                    control={<Checkbox size="small" checked={membershipIds.includes(membershipId)} onChange={() => toggleMembership(membershipId)} />}
                                                    label={`${studentName}${student.studentId ? ` · ${student.studentId}` : ''}`}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}

                    {kind === 'subAdmins' && (
                        <>
                            <TextField label="Role title" required size="small" value={form.roleTitle} onChange={setField('roleTitle')} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Dashboard access</Typography>
                                <FormControlLabel control={<Checkbox size="small" checked disabled />} label="Overview" />
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                    {SUB_ADMIN_ACCESS_OPTIONS.map(([key, label]) => (
                                        <FormControlLabel
                                            key={key}
                                            control={<Checkbox size="small" checked={accessTabs.includes(key)} onChange={() => toggleAccess(key)} />}
                                            label={label}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={saving}>
                        {saving ? 'Saving…' : 'Save and Resend'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

function SchoolActivityDetailDialog({ open, schoolId, schoolName, onClose }) {
    const setError = useErrorSetter();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(() => activityDateValue(-29));
    const [endDate, setEndDate] = useState(() => activityDateValue());
    const [eventPage, setEventPage] = useState(1);
    const [eventCategory, setEventCategory] = useState('');
    const [notice, setNotice] = useState(null);
    const [editTarget, setEditTarget] = useState(null);

    const fetchDetail = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const params = { startDate, endDate, eventsPage: eventPage, eventsLimit: 50 };
            if (eventCategory) params.category = eventCategory;
            const resp = await axios.get(`/origin/schools/${schoolId}/activity`, {
                params,
                headers: { 'x-access-token': token() }
            });
            setData(resp.data);
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to load school activity');
        } finally {
            setLoading(false);
        }
    }, [schoolId, startDate, endDate, eventPage, eventCategory, setError]);

    useEffect(() => {
        if (open) fetchDetail();
    }, [open, fetchDetail]);

    useEffect(() => {
        if (open) {
            setEventPage(1);
            setEventCategory('');
            setNotice(null);
        }
    }, [open, schoolId]);

    const handleResend = (kind, invite) => setEditTarget({ kind, actor: invite });

    const eventPagination = data?.recentEventsPagination || { total: 0, page: 1, limit: 50 };
    const eventPages = Math.max(1, Math.ceil((eventPagination.total || 0) / (eventPagination.limit || 50)));
    const categoryData = (data?.eventCategoryCounts || []).map(item => ({ ...item, label: item.label || item.category }));
    const trendData = data?.dailyActivityTrend || [];

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle>School Activity — {data?.school?.name || schoolName || 'School'}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                        label="Activity from"
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={e => { setStartDate(e.target.value); setEventPage(1); }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Activity to"
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={e => { setEndDate(e.target.value); setEventPage(1); }}
                        InputLabelProps={{ shrink: true }}
                    />
                    {notice && (
                        <Typography variant="body2" color={notice.ok ? 'success.main' : 'warning.main'}>
                            {notice.text}
                        </Typography>
                    )}
                </Box>

                {loading && !data ? <OvalSpinner /> : data ? (
                    <>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Invites</Typography>
                        <ActivityInviteTable title="Teachers" payload={data.invites?.teachers} kind="teachers" onResend={handleResend} />
                        <ActivityInviteTable title="Parents" payload={data.invites?.parents} kind="parents" onResend={handleResend} />
                        <ActivityInviteTable title="Sub-admins" payload={data.invites?.subAdmins} kind="subAdmins" onResend={handleResend} />
                        <ActivityInviteTable title="School admins" payload={data.invites?.schoolAdmins} kind="schoolAdmins" onResend={handleResend} canResend={false} />

                        <ExamBreakdownPanel exams={data.exams} />

                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Activity by Module</Typography>
                        <Box sx={{ height: 260, mb: 3, minWidth: 0 }}>
                            {categoryData.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No activity in this date range.</Typography>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ActivityBarChart data={categoryData} margin={{ top: 8, right: 20, left: 0, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="label" angle={-30} textAnchor="end" interval={0} />
                                        <YAxis allowDecimals={false} />
                                        <ActivityChartTooltip />
                                        <ActivityBar dataKey="count" fill="#3e8f93" />
                                    </ActivityBarChart>
                                </ResponsiveContainer>
                            )}
                        </Box>

                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Daily Activity</Typography>
                        <Box sx={{ height: 240, mb: 3, minWidth: 0 }}>
                            {trendData.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No daily activity in this date range.</Typography>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ActivityLineChart data={trendData} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis allowDecimals={false} />
                                        <ActivityChartTooltip />
                                        <ActivityLine type="monotone" dataKey="count" stroke="#d97706" strokeWidth={2} dot={false} />
                                    </ActivityLineChart>
                                </ResponsiveContainer>
                            )}
                        </Box>

                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Bulk Invite Jobs</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ background: '#f5f7fa' }}>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Total</strong></TableCell>
                                        <TableCell><strong>Processed</strong></TableCell>
                                        <TableCell><strong>Succeeded</strong></TableCell>
                                        <TableCell><strong>Failed</strong></TableCell>
                                        <TableCell><strong>Created</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[
                                        ...(data.bulkJobs?.teachers || []).map(job => ({ ...job, jobType: 'Teachers' })),
                                        ...(data.bulkJobs?.parents || []).map(job => ({ ...job, jobType: 'Parents' }))
                                    ].length === 0 ? (
                                        <TableRow><TableCell colSpan={7} align="center">No bulk invite jobs found</TableCell></TableRow>
                                    ) : [
                                        ...(data.bulkJobs?.teachers || []).map(job => ({ ...job, jobType: 'Teachers' })),
                                        ...(data.bulkJobs?.parents || []).map(job => ({ ...job, jobType: 'Parents' }))
                                    ].map(job => (
                                        <TableRow key={`${job.jobType}-${job._id}`} hover>
                                            <TableCell>{job.jobType}</TableCell>
                                            <TableCell><Chip size="small" label={job.status} color={job.status === 'failed' ? 'error' : job.status === 'done' ? 'success' : 'warning'} /></TableCell>
                                            <TableCell>{job.totals?.total || 0}</TableCell>
                                            <TableCell>{job.totals?.processed || 0}</TableCell>
                                            <TableCell>{job.totals?.succeeded || 0}</TableCell>
                                            <TableCell>{job.totals?.failed || 0}</TableCell>
                                            <TableCell>{formatActivityDate(job.createdAt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>Recent Events</Typography>
                            <Select
                                value={eventCategory}
                                onChange={e => { setEventCategory(e.target.value); setEventPage(1); }}
                                displayEmpty
                                size="small"
                                sx={{ minWidth: 180 }}
                            >
                                <MenuItem value="">All modules</MenuItem>
                                {categoryData.map(item => (
                                    <MenuItem key={item.category} value={item.category}>{item.label}</MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ background: '#f5f7fa' }}>
                                        <TableCell><strong>Time</strong></TableCell>
                                        <TableCell><strong>Module</strong></TableCell>
                                        <TableCell><strong>Event</strong></TableCell>
                                        <TableCell><strong>Details</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.recentEvents || []).length === 0 ? (
                                        <TableRow><TableCell colSpan={4} align="center">No events found in this date range</TableCell></TableRow>
                                    ) : data.recentEvents.map((event, index) => (
                                        <TableRow key={`${event.timestamp}-${event.eventType}-${index}`} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatActivityDate(event.timestamp, true)}</TableCell>
                                            <TableCell><Chip size="small" label={event.category} variant="outlined" /></TableCell>
                                            <TableCell>{event.eventType}</TableCell>
                                            <TableCell sx={{ maxWidth: 520, wordBreak: 'break-word' }}>{formatActivityData(event.eventData) || '–'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {eventPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                                <Button size="small" disabled={eventPage <= 1} onClick={() => setEventPage(page => page - 1)}>Prev</Button>
                                <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {eventPage} / {eventPages}</Typography>
                                <Button size="small" disabled={eventPage >= eventPages} onClick={() => setEventPage(page => page + 1)}>Next</Button>
                            </Box>
                        )}
                    </>
                ) : null}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
        <InviteResendEditDialog
            target={editTarget}
            schoolId={schoolId}
            students={data?.students?.records || []}
            onClose={() => setEditTarget(null)}
            onSaved={async result => {
                setEditTarget(null);
                setNotice({
                    text: result?.message || 'Invite updated and resent',
                    ok: Boolean(result?.emailSent)
                });
                await fetchDetail();
            }}
        />
        </>
    );
}

function ActivityOverviewPanel() {
    const setError = useErrorSetter();
    const [data, setData] = useState({ platformTotals: {}, schools: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [startDate, setStartDate] = useState(() => activityDateValue(-29));
    const [endDate, setEndDate] = useState(() => activityDateValue());
    const [detailSchool, setDetailSchool] = useState(null);
    const LIMIT = 20;

    const fetchActivity = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: LIMIT, startDate, endDate };
            if (search.trim()) params.search = search.trim();
            if (status) params.status = status;
            const resp = await axios.get('/origin/schools/activity/overview', {
                params,
                headers: { 'x-access-token': token() }
            });
            setData(resp.data || { platformTotals: {}, schools: [], total: 0 });
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to load school activity overview');
        } finally {
            setLoading(false);
        }
    }, [page, search, status, startDate, endDate, setError]);

    useEffect(() => { fetchActivity(); }, [fetchActivity]);

    const totals = data.platformTotals || {};
    const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));
    const metricCards = [
        { label: 'Total students', value: totals.students?.total || 0, color: '#1565c0' },
        { label: 'Total exams', value: totals.exams?.total || 0, color: '#6a1b9a' },
        { label: 'CBT exams', value: totals.exams?.byMode?.cbt || 0, color: '#1565c0' },
        { label: 'Manual + AI grading', value: totals.exams?.byMode?.manual || 0, color: '#6a1b9a' },
        { label: 'Manual score entry', value: totals.exams?.byMode?.manual_scores || 0, color: '#b45309' },
        { label: 'Other / legacy mode', value: totals.exams?.byMode?.other || 0, color: '#6b7280' },
        { label: 'Expired invites', value: totals.expiredInvites || 0, color: '#c62828' },
        { label: 'Active teachers', value: totals.teachers?.active || 0, color: '#2e7d32' },
        { label: 'Pending teacher invites', value: totals.teachers?.pending || 0, color: '#b45309' },
        { label: 'Active parents', value: totals.parents?.active || 0, color: '#2e7d32' },
        { label: 'Pending parent invites', value: totals.parents?.pending || 0, color: '#b45309' },
        { label: 'Active sub-admins', value: totals.subAdmins?.active || 0, color: '#2e7d32' },
        { label: 'Pending sub-admin invites', value: totals.subAdmins?.pending || 0, color: '#b45309' },
        { label: 'Active school admins', value: totals.schoolAdmins?.active || 0, color: '#2e7d32' },
        { label: 'Pending school-admin invites', value: totals.schoolAdmins?.pending || 0, color: '#b45309' },
        { label: 'Events in range', value: totals.events?.totalInRange || 0, color: '#3e8f93' },
        { label: 'Schools without activity', value: Math.max(0, (totals.schools?.total || 0) - (totals.events?.schoolsWithActivity || 0)), color: '#6b7280' }
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                    Invite status is a current snapshot. Activity totals and event charts use the selected date range and include every event tagged to a school.
                </Typography>
                <Tooltip title="Refresh activity">
                    <IconButton onClick={fetchActivity} disabled={loading} sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    placeholder="Search schools…"
                    size="small"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    sx={{ minWidth: 220, bgcolor: 'white' }}
                />
                <Select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} displayEmpty size="small" sx={{ minWidth: 150, bgcolor: 'white' }}>
                    <MenuItem value="">All statuses</MenuItem>
                    {['active', 'pending', 'suspended'].map(value => <MenuItem key={value} value={value}>{value.charAt(0).toUpperCase() + value.slice(1)}</MenuItem>)}
                </Select>
                <TextField label="Events from" type="date" size="small" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} />
                <TextField label="Events to" type="date" size="small" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} />
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {metricCards.map(card => (
                    <Grid item xs={6} sm={4} md={3} key={card.label}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ color: card.color }}>{Number(card.value).toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {loading ? <OvalSpinner /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: '#f5f7fa' }}>
                                <TableCell><strong>School</strong></TableCell>
                                <TableCell><strong>Status / Plan</strong></TableCell>
                                <TableCell><strong>Students</strong></TableCell>
                                <TableCell><strong>Exams</strong></TableCell>
                                <TableCell><strong>Teachers</strong></TableCell>
                                <TableCell><strong>Parents</strong></TableCell>
                                <TableCell><strong>Sub-admins</strong></TableCell>
                                <TableCell><strong>School admins</strong></TableCell>
                                <TableCell><strong>Events</strong></TableCell>
                                <TableCell><strong>Last activity</strong></TableCell>
                                <TableCell align="right"><strong>Details</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data.schools || []).length === 0 ? (
                                <TableRow><TableCell colSpan={11} align="center">No schools found</TableCell></TableRow>
                            ) : data.schools.map(school => {
                                const teachers = school.invites?.teachers || {};
                                const parents = school.invites?.parents || {};
                                const subAdmins = school.invites?.subAdmins || {};
                                const schoolAdmins = school.invites?.schoolAdmins || {};
                                return (
                                    <TableRow key={String(school.schoolId)} hover>
                                        <TableCell><Typography variant="body2" fontWeight={700}>{school.name}</Typography></TableCell>
                                        <TableCell>
                                            <Chip size="small" label={school.status} color={STATUS_COLORS[school.status] || 'default'} />
                                            <Chip size="small" sx={{ ml: 0.5 }} label={school.plan || '–'} variant="outlined" color={PLAN_COLORS[school.plan] || 'default'} />
                                        </TableCell>
                                        <TableCell>{school.students || 0}</TableCell>
                                        <TableCell><ExamSummaryCell exams={school.exams} /></TableCell>
                                        <TableCell>{teachers.active || 0} / {teachers.pending || 0}</TableCell>
                                        <TableCell>{parents.active || 0} / {parents.pending || 0}</TableCell>
                                        <TableCell>{subAdmins.active || 0} / {subAdmins.pending || 0}</TableCell>
                                        <TableCell>{schoolAdmins.active || 0} / {schoolAdmins.pending || 0}</TableCell>
                                        <TableCell>{school.events?.totalInRange || 0}</TableCell>
                                        <TableCell>{formatActivityDate(school.events?.lastActivityAt, true)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View school activity">
                                                <IconButton size="small" onClick={() => setDetailSchool({ id: school.schoolId, name: school.name })}>
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    <Button size="small" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Prev</Button>
                    <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {page} / {totalPages}</Typography>
                    <Button size="small" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>Next</Button>
                </Box>
            )}

            <SchoolActivityDetailDialog
                open={Boolean(detailSchool)}
                schoolId={detailSchool?.id}
                schoolName={detailSchool?.name}
                onClose={() => setDetailSchool(null)}
            />
        </Box>
    );
}

const SchoolsDashboard = () => {
    const navigate = useNavigate();
    const setError = useErrorSetter();

    // ── View tab: 'schools' | 'requests' ────────────────────────────────────
    const [viewTab, setViewTab] = useState('schools');

    const [schools, setSchools] = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(1);
    const [loading, setLoading] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [planFilter, setPlanFilter]     = useState('');

    // Create-school dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm]             = useState(EMPTY_FORM);
    const [creating, setCreating]     = useState(false);

    // Subscription dialog
    const [subOpen, setSubOpen]       = useState(false);
    const [subSchool, setSubSchool]   = useState(null);
    const [passwordResetSchool, setPasswordResetSchool] = useState(null);
    const [statusSchool, setStatusSchool] = useState(null);
    const [invoiceSchool, setInvoiceSchool] = useState(null);
    const [adminRole, setAdminRole] = useState('');

    // ── Requests state ───────────────────────────────────────────────────────
    const [requests, setRequests]           = useState([]);
    const [reqTotal, setReqTotal]           = useState(0);
    const [reqPage, setReqPage]             = useState(1);
    const [reqLoading, setReqLoading]       = useState(false);
    const [reqStatusFilter, setReqStatusFilter] = useState('');
    const [reqDetail, setReqDetail]         = useState(null);
    const [reqDetailOpen, setReqDetailOpen] = useState(false);

    const LIMIT = 30;

    useEffect(() => {
        let cancelled = false;
        axios.get('/origin/get-current-admin', { headers: { 'x-access-token': token() } })
            .then(response => {
                if (!cancelled) setAdminRole(response.data.role || '');
            })
            .catch(() => {
                if (!cancelled) setAdminRole('');
            });
        return () => { cancelled = true; };
    }, []);

    const fetchSchools = useCallback(async () => {
        try {
            await checkAuthenticatedAdmin();
        } catch {
            return navigate('/schools');
        }
        setLoading(true);
        try {
            const p = new URLSearchParams({ page, limit: LIMIT });
            if (statusFilter) p.set('status', statusFilter);
            if (planFilter)   p.set('plan', planFilter);
            const resp = await axios.get(`/origin/schools?${p.toString()}`, {
                headers: { 'x-access-token': token() }
            });
            setSchools(resp.data.schools || []);
            setTotal(resp.data.total || 0);
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to fetch schools');
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, planFilter, navigate, setError]);

    useEffect(() => { fetchSchools(); }, [fetchSchools]);

    // ── Requests fetch ───────────────────────────────────────────────────────
    const fetchRequests = useCallback(async () => {
        setReqLoading(true);
        try {
            const p = new URLSearchParams({ page: reqPage, limit: LIMIT });
            if (reqStatusFilter) p.set('status', reqStatusFilter);
            const resp = await axios.get(`/origin/school-requests?${p.toString()}`, {
                headers: { 'x-access-token': token() }
            });
            setRequests(resp.data.requests || []);
            setReqTotal(resp.data.total || 0);
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to fetch requests');
        } finally {
            setReqLoading(false);
        }
    }, [reqPage, reqStatusFilter, setError]);

    useEffect(() => { if (viewTab === 'requests') fetchRequests(); }, [viewTab, fetchRequests]);

    const handleRequestStatusChange = async (id, status, reviewNotes) => {
        try {
            await axios.patch(`/origin/school-requests/${id}/status`, { status, reviewNotes }, {
                headers: { 'x-access-token': token() }
            });
            fetchRequests();
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to update request');
        }
    };

    // Pre-fill Create School form from a request then open the dialog
    const handleConvertRequest = (req) => {
        setForm({
            name:           req.schoolName || '',
            contactEmail:   req.contactEmail || '',
            phone:          req.contactPhone || '',
            address:        req.address || '',
            type:           req.schoolType || 'secondary',
            country:        req.country || 'Nigeria',
            state:          req.state || '',
            academicSession: '',
            currentTerm:    'first',
            adminFirstName: req.contactFirstName || '',
            adminLastName:  req.contactLastName  || '',
            adminEmail:     req.contactEmail     || '',
            adminPhone:     req.contactPhone     || '',
            plan:           req.plan || 'premium',
            requestId:      req._id || ''
        });
        setCreateOpen(true);
    };

    const handleStatusChange = async (schoolId, newStatus) => {
        try {
            await axios.patch(`/origin/schools/${schoolId}/status`, { status: newStatus }, {
                headers: { 'x-access-token': token() }
            });
            fetchSchools();
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to update status');
        }
    };

    const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const handleCreate = async () => {
        const required = ['name','contactEmail','adminFirstName','adminLastName','adminEmail'];
        for (const k of required) {
            if (!form[k]?.trim()) {
                setError(`"${k}" is required`); return;
            }
        }
        setCreating(true);
        try {
            const body = { ...form };
            if (!body.requestId) delete body.requestId;
            await axios.post('/origin/schools/create', body, {
                headers: { 'x-access-token': token() }
            });
            setCreateOpen(false);
            setForm(EMPTY_FORM);
            fetchSchools();
            if (viewTab === 'requests') fetchRequests();
        } catch (e) {
            setError(e?.response?.data?.error || 'Failed to create school');
        } finally {
            setCreating(false);
        }
    };

    const totalPages    = Math.max(1, Math.ceil(total / LIMIT));
    const reqTotalPages = Math.max(1, Math.ceil(reqTotal / LIMIT));

    // Analytics.module.css darkens this container in OS dark mode, but the MUI text on this page is light-theme only.
    return (
        <div className={styles.analyticsContainer} style={{ backgroundColor: '#f5f7fa', colorScheme: 'light' }}>
            <AdminSideMenu />
            <div className={styles.analyticsContent}>

                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <div>
                        <Typography variant="h5" fontWeight={700} sx={{ color: '#1a1a1a' }}>Schools</Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {viewTab === 'schools'
                                ? `${total} school${total !== 1 ? 's' : ''} registered`
                                : viewTab === 'requests'
                                    ? `${reqTotal} interest request${reqTotal !== 1 ? 's' : ''}`
                                    : 'Platform-wide school activity and invite oversight'}
                        </Typography>
                    </div>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton
                                onClick={viewTab === 'schools' ? fetchSchools : viewTab === 'requests' ? fetchRequests : undefined}
                                disabled={viewTab === 'activity'}
                                sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', color: '#1a1a1a', '&:hover': { bgcolor: '#f5f7fa' } }}
                            ><Refresh /></IconButton>
                        </Tooltip>
                        {viewTab === 'schools' && (
                            <Button variant="contained" startIcon={<Add />} onClick={() => { setForm(EMPTY_FORM); setCreateOpen(true); }}
                                sx={{ background: '#3e8f93', '&:hover': { background: '#2c6b6f' } }}>
                                Create School
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* View tabs */}
                <Tabs
                    value={viewTab}
                    onChange={(_, v) => setViewTab(v)}
                    sx={{
                        mb: 3,
                        borderBottom: '1px solid #e5e7eb',
                        bgcolor: '#fff',
                        borderRadius: '8px 8px 0 0',
                        '& .MuiTab-root': { color: '#555', fontWeight: 600, textTransform: 'none' },
                        '& .MuiTab-root.Mui-selected': { color: '#3e8f93' },
                        '& .MuiTabs-indicator': { backgroundColor: '#3e8f93' }
                    }}
                >
                    <Tab value="schools"  label="Schools"  icon={<Add fontSize="small" />}  iconPosition="start" />
                    <Tab value="requests" label="Requests" icon={<Inbox fontSize="small" />} iconPosition="start" />
                    <Tab value="pricing"  label="Plan Pricing" icon={<PriceCheck fontSize="small" />} iconPosition="start" />
                    <Tab value="revenue"  label="Revenue" icon={<PriceCheck fontSize="small" />} iconPosition="start" />
                    <Tab value="waivers"  label="Fee Waivers" icon={<AssignmentTurnedIn fontSize="small" />} iconPosition="start" />
                    <Tab value="activity" label="Activity" icon={<Timeline fontSize="small" />} iconPosition="start" />
                </Tabs>

                {/* ══════════ SCHOOLS VIEW ══════════ */}
                {viewTab === 'schools' && (<>
                {/* Stat cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {['active','pending','suspended'].map(s => (
                        <Grid item xs={6} sm={4} key={s}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary" textTransform="capitalize">{s}</Typography>
                                    <Typography variant="h5" fontWeight={700}>
                                        {schools.filter(sc => sc.status === s).length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                        displayEmpty size="small" sx={{ minWidth: 140, bgcolor: 'white', color: '#1a1a1a' }}>
                        <MenuItem value="">All Statuses</MenuItem>
                        {['active','pending','suspended'].map(s => (
                            <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                        ))}
                    </Select>
                    <Select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
                        displayEmpty size="small" sx={{ minWidth: 140, bgcolor: 'white', color: '#1a1a1a' }}>
                        <MenuItem value="">All Plans</MenuItem>
                        {PLANS.map(p => (
                            <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
                        ))}
                    </Select>
                </Box>

                {/* Table */}
                {loading ? <OvalSpinner /> : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ background: '#f5f7fa' }}>
                                    <TableCell />
                                    <TableCell><strong>Name</strong></TableCell>
                                    <TableCell><strong>Email</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Plan</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>State</strong></TableCell>
                                    <TableCell><strong>Active Students</strong></TableCell>
                                    <TableCell><strong>Teachers</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {schools.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">No schools found</TableCell>
                                    </TableRow>
                                ) : schools.map(school => (
                                    <SchoolRow
                                        key={school._id}
                                        school={school}
                                        onStatusChange={handleStatusChange}
                                        onEditSubscription={s => { setSubSchool(s); setSubOpen(true); }}
                                        onResetStudentPasswords={setPasswordResetSchool}
                                        canResetStudents={['superadmin', 'admin'].includes(adminRole)}
                                        onManageStudentStatus={setStatusSchool}
                                        onOpenInvoices={setInvoiceSchool}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                        <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                        <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {page} / {totalPages}</Typography>
                        <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                    </Box>
                )}
                </>)}

                {/* ══════════ REQUESTS VIEW ══════════ */}
                {viewTab === 'requests' && (<>
                    {/* Status filter */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Select value={reqStatusFilter} onChange={e => { setReqStatusFilter(e.target.value); setReqPage(1); }}
                            displayEmpty size="small" sx={{ minWidth: 160, bgcolor: 'white', color: '#1a1a1a' }}>
                            <MenuItem value="">All Statuses</MenuItem>
                            {REQ_STATUSES.map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
                        </Select>
                    </Box>

                    {reqLoading ? <OvalSpinner /> : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ background: '#f5f7fa' }}>
                                        <TableCell><strong>School</strong></TableCell>
                                        <TableCell><strong>Contact</strong></TableCell>
                                        <TableCell><strong>State</strong></TableCell>
                                        <TableCell><strong>Plan</strong></TableCell>
                                        <TableCell><strong>Est. Students</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {requests.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">No requests found</TableCell>
                                        </TableRow>
                                    ) : requests.map(r => (
                                        <TableRow key={r._id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{r.schoolName}</Typography>
                                                <Typography variant="caption" color="text.secondary">{r.schoolType}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{r.contactFirstName} {r.contactLastName}</Typography>
                                                <Typography variant="caption" color="text.secondary">{r.contactEmail}</Typography>
                                            </TableCell>
                                            <TableCell>{r.state}</TableCell>
                                            <TableCell>
                                                <Chip label={r.plan} color={PLAN_COLORS[r.plan] || 'default'} size="small" />
                                            </TableCell>
                                            <TableCell>{r.estimatedStudents || '–'}</TableCell>
                                            <TableCell>
                                                <Chip label={r.status} color={REQ_STATUS_COLORS[r.status]} size="small" />
                                            </TableCell>
                                            <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '–'}</TableCell>
                                            <TableCell>
                                                <Tooltip title="View / Review">
                                                    <IconButton size="small" onClick={() => { setReqDetail(r); setReqDetailOpen(true); }}>
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Convert to School">
                                                    <IconButton size="small" color="primary" onClick={() => { handleConvertRequest(r); }}>
                                                        <HowToReg fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject">
                                                    <IconButton size="small" color="error"
                                                        onClick={() => handleRequestStatusChange(r._id, 'rejected', '')}>
                                                        <Cancel fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {reqTotalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                            <Button size="small" disabled={reqPage <= 1} onClick={() => setReqPage(p => p - 1)}>Prev</Button>
                            <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {reqPage} / {reqTotalPages}</Typography>
                            <Button size="small" disabled={reqPage >= reqTotalPages} onClick={() => setReqPage(p => p + 1)}>Next</Button>
                        </Box>
                    )}
                </>)}

                {/* ══════════ PLAN PRICING VIEW ══════════ */}
                {viewTab === 'pricing' && <PlanPricingPanel />}

                {/* ══════════ REVENUE VIEW ══════════ */}
                {viewTab === 'revenue' && <RevenuePanel />}

                {/* ══════════ FEE WAIVERS VIEW ══════════ */}
                {viewTab === 'waivers' && <FeeWaiversPanel schoolsList={schools} />}

                {/* ══════════ ACTIVITY VIEW ══════════ */}
                {viewTab === 'activity' && <ActivityOverviewPanel />}
            </div>

            {/* ── Create School Dialog ───────────────────────────────────────── */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New School</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">School Details</Typography>
                    <TextField label="School Name *" size="small" value={form.name} onChange={setField('name')} />
                    <TextField label="Contact Email *" size="small" type="email" value={form.contactEmail} onChange={setField('contactEmail')} />
                    <TextField label="Phone" size="small" value={form.phone} onChange={setField('phone')} />
                    <TextField label="Address" size="small" value={form.address} onChange={setField('address')} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Select value={form.type} onChange={setField('type')} size="small" fullWidth>
                            {SCHOOL_TYPES.map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
                        </Select>
                        <Select value={form.currentTerm} onChange={setField('currentTerm')} size="small" fullWidth>
                            {TERMS.map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)} Term</MenuItem>)}
                        </Select>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Country" size="small" fullWidth value={form.country} onChange={setField('country')} />
                        <TextField label="State" size="small" fullWidth value={form.state} onChange={setField('state')} />
                    </Box>
                    <TextField label="Academic Session (e.g. 2025/2026)" size="small" value={form.academicSession} onChange={setField('academicSession')} />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Admin Account</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="First Name *" size="small" fullWidth value={form.adminFirstName} onChange={setField('adminFirstName')} />
                        <TextField label="Last Name *" size="small" fullWidth value={form.adminLastName} onChange={setField('adminLastName')} />
                    </Box>
                    <TextField label="Admin Email *" size="small" type="email" value={form.adminEmail} onChange={setField('adminEmail')} />
                    <TextField label="Admin Phone" size="small" value={form.adminPhone} onChange={setField('adminPhone')} />

                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Subscription Plan</Typography>
                    <Select value={form.plan} onChange={setField('plan')} size="small">
                        {PLANS.map(p => <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>)}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={creating}
                        sx={{ background: '#3e8f93', '&:hover': { background: '#2c6b6f' } }}>
                        {creating ? 'Creating…' : 'Create School'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Subscription Dialog ────────────────────────────────────────── */}
            <SubscriptionDialog
                open={subOpen}
                school={subSchool}
                onClose={() => setSubOpen(false)}
                onSaved={fetchSchools}
            />

            {passwordResetSchool && (
                <StudentPasswordResetDialog
                    school={passwordResetSchool}
                    onClose={() => setPasswordResetSchool(null)}
                />
            )}

            {statusSchool && (
                <StudentStatusDialog
                    school={statusSchool}
                    canEdit={['superadmin', 'admin'].includes(adminRole)}
                    onClose={() => setStatusSchool(null)}
                    onChanged={fetchSchools}
                />
            )}

            {invoiceSchool && (
                <SubscriptionInvoiceDialog
                    school={invoiceSchool}
                    canEdit={['superadmin', 'admin'].includes(adminRole)}
                    onClose={() => setInvoiceSchool(null)}
                />
            )}

            {/* ── Request Detail Dialog ──────────────────────────────────────── */}
            <RequestDetailDialog
                open={reqDetailOpen}
                request={reqDetail}
                onClose={() => setReqDetailOpen(false)}
                onStatusChange={handleRequestStatusChange}
                onConvert={(req) => { setReqDetailOpen(false); handleConvertRequest(req); }}
            />
        </div>
    );
};

export default SchoolsDashboard;
